import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, X } from "lucide-react";
import { formatDrawCoin } from "../../utils/bondDrawHelpers";

const QUICK_PICKS = [1000, 5000, 10000, 20000];

const BondDrawInvestModal = ({
  open = false,
  maxAmount = 0,
  loading = false,
  isExistingInvestor = false,
  onClose,
  onSubmit,
}) => {
  const [amount, setAmount] = useState("");

  useEffect(() => {
    if (!open) setAmount("");
  }, [open]);

  const parsed = useMemo(() => Math.floor(Number(amount || 0)), [amount]);
  const max = Math.max(0, Math.floor(Number(maxAmount || 0)));
  const clamped = useMemo(() => {
    if (!Number.isFinite(parsed) || parsed <= 0) return 0;
    return Math.min(parsed, max);
  }, [parsed, max]);

  const quickPicks = useMemo(
    () => QUICK_PICKS.filter((value) => value <= max),
    [max]
  );

  if (!open) return null;

  const disabled = loading || !Number.isFinite(clamped) || clamped <= 0;
  const title = isExistingInvestor ? "Add Balance" : "Join Investor Pool";
  const submitLabel = isExistingInvestor ? "Add to pool" : "Invest";

  return createPortal(
    <div className="bdraw-cashout-root" role="dialog" aria-modal="true" aria-labelledby="bdraw-invest-title">
      <button type="button" className="bdraw-cashout-backdrop" aria-label="Close" disabled={loading} onClick={onClose} />
      <div className="bdraw-cashout-sheet bdraw-invest-sheet">
        <header className="bdraw-cashout-head">
          <div>
            <p className="bdraw-cashout-kicker">Investor pool</p>
            <h2 id="bdraw-invest-title" className="bdraw-cashout-title">{title}</h2>
            <p className="bdraw-cashout-subtitle">
              Wallet: {formatDrawCoin(max)} COIN available
            </p>
          </div>
          <button type="button" className="bdraw-cashout-close" aria-label="Close" disabled={loading} onClick={onClose}>
            <X size={16} strokeWidth={2.5} />
          </button>
        </header>

        <label className="bdraw-cashout-label" htmlFor="bdraw-invest-amount">
          Amount (COIN)
        </label>
        <input
          id="bdraw-invest-amount"
          className="bdraw-cashout-input"
          type="number"
          min="1"
          max={max || undefined}
          placeholder="e.g. 5000"
          value={amount}
          disabled={loading}
          onChange={(e) => setAmount(e.target.value)}
        />

        {quickPicks.length > 0 ? (
          <div className="bdraw-cashout-quick bdraw-invest-quick">
            {quickPicks.map((value) => (
              <button
                key={value}
                type="button"
                className="bdraw-cashout-quick-btn"
                disabled={loading}
                onClick={() => setAmount(String(value))}
              >
                {formatDrawCoin(value)}
              </button>
            ))}
          </div>
        ) : null}

        <div className="bdraw-cashout-actions">
          <button type="button" className="bdraw-cashout-btn secondary" disabled={loading} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="bdraw-cashout-btn primary"
            disabled={disabled}
            onClick={() => onSubmit(clamped)}
          >
            {loading ? (
              <>
                <Loader2 size={14} className="bdraw-spin" />
                Processing…
              </>
            ) : (
              submitLabel
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default BondDrawInvestModal;
